import { Fragment, useCallback, useEffect, useMemo, useState } from "react";
import { Container } from "reactstrap";
import { useSelector } from "react-redux";
import { Copy, Edit, Plus, Power, RefreshCw } from "react-feather";
import Breadcrumb from "../common/breadcrumb";
import { getBrands, deactivateBrand, updateBrand, brandMenuUrl } from "../../utils/brandApi";
import { successToast, errorToast } from "../../shared/Toast";
import { C, tbl, reportingGlobalCSS } from "../reporting/reportingStyles";
import { RCard, Badge, Loader as RLoader, TablePagination, usePagination } from "../reporting/ReportingShared";
import BrandModal from "./BrandModal";

const coralBtn = {
  background: C.coral, border: "none", borderRadius: 10, fontWeight: 600, fontSize: 13,
  color: C.white, padding: "8px 16px", cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 6,
};
const iconBtn = {
  background: C.white, border: `1px solid ${C.border}`, borderRadius: 8, fontWeight: 600, fontSize: 12,
  color: C.textMed, padding: "5px 10px", cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 5,
};
const searchInput = {
  padding: "9px 14px", borderRadius: 10, border: `1.5px solid ${C.border}`, fontSize: 13,
  width: 240, outline: "none", fontFamily: "inherit",
};

// Consumer brands for the selected merchant.
//
// A brand is a tag put on products and branches; a Pickup App is then configured with a
// set of brands (Apps screen) and, with its brandBasedBranches / brandBasedProducts
// switches on, only sees branches and products carrying them.
//
// Two things worth knowing when reading this screen:
//   - Product tags come from the merchant system (each product's brand_ids), not from
//     here. This page manages the brand records themselves; branch tags are set on the
//     branch edit screen and app tags on the app edit screen.
//   - Brands are never deleted. Deactivating drops a brand out of every app's filter
//     while keeping its tags, so it can be switched straight back on.
const BrandsList = () => {
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [modal, setModal] = useState({ open: false, row: null });

  // Standard merchant-scoping idiom: a merchant user is pinned to its own merchant, an
  // admin picks one from the header selector.
  const userType = localStorage.getItem("userType");
  const storedMerchantId = localStorage.getItem("MerchantId");
  const { selectedMerchant: reduxSelectedMerchant } = useSelector((state) => state.merchant || {});
  const selectedMerchant = userType === "merchant" ? storedMerchantId : reduxSelectedMerchant;

  const load = useCallback(async () => {
    if (!selectedMerchant || selectedMerchant === "all") {
      setBrands([]);
      return;
    }
    setLoading(true);
    const data = await getBrands(selectedMerchant);
    setBrands(Array.isArray(data) ? data : []);
    setLoading(false);
  }, [selectedMerchant]);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return brands;
    return brands.filter(
      (b) =>
        b?.name?.toLowerCase().includes(q) ||
        b?.arabicName?.toLowerCase().includes(q) ||
        String(b?.merchant_brand_id ?? "").toLowerCase().includes(q) ||
        b?.slug?.toLowerCase().includes(q)
    );
  }, [brands, search]);

  const pg = usePagination(filtered, 10);

  const handleDeactivate = async (row) => {
    const message =
      `Deactivate "${row.name}"?\n\n` +
      `It will disappear from every app that filters by it on the next request. ` +
      `Its product and branch tags are kept, so you can switch it back on any time.`;
    if (!window.confirm(message)) return;

    const result = await deactivateBrand(row.id);
    if (result) {
      successToast(`${row.name} deactivated`);
      load();
    }
  };

  const handleReactivate = async (row) => {
    const result = await updateBrand(row.id, { active: true });
    if (result) {
      successToast(`${row.name} reactivated`);
      load();
    }
  };

  const copyLink = async (url) => {
    try {
      await navigator.clipboard.writeText(url);
      successToast("Link copied");
    } catch {
      errorToast("Copy failed — please copy manually");
    }
  };

  const needsMerchant = !selectedMerchant || selectedMerchant === "all";

  return (
    <Fragment>
      <Breadcrumb title="Brands" parent="Brands" />
      <Container fluid>
        <style>{reportingGlobalCSS}</style>
        <div className="rpt-wrap">
          <RCard>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, marginBottom: 18, flexWrap: "wrap" }}>
              <div>
                <h5 style={{ margin: 0, color: C.text, fontWeight: 700 }}>Brands</h5>
                <div style={{ fontSize: 12, color: C.textLight, marginTop: 4 }}>
                  Tag products and branches with a brand, then point a Pickup App at it from
                  the Apps screen. Give a brand a menu link slug to share a public menu page
                  listing only its branches.
                </div>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <input
                  style={searchInput}
                  placeholder="Search brands…"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
                <button style={iconBtn} onClick={load} disabled={loading}>
                  <RefreshCw size={13} /> Refresh
                </button>
                <button
                  style={{ ...coralBtn, opacity: needsMerchant ? 0.6 : 1, cursor: needsMerchant ? "not-allowed" : "pointer" }}
                  disabled={needsMerchant}
                  onClick={() => setModal({ open: true, row: null })}
                >
                  <Plus size={14} /> Add Brand
                </button>
              </div>
            </div>

            {needsMerchant ? (
              <div style={{ padding: "32px 0", textAlign: "center", fontSize: 13, color: C.textLight }}>
                Select a merchant to manage its brands.
              </div>
            ) : loading ? (
              <RLoader />
            ) : filtered.length === 0 ? (
              <div style={{ padding: "32px 0", textAlign: "center", fontSize: 13, color: C.textLight }}>
                {brands.length === 0
                  ? "No brands yet. They arrive with the next menu sync, or add one by hand."
                  : "No brands match that search."}
              </div>
            ) : (
              <>
                <div style={{ overflowX: "auto" }}>
                  <table style={tbl.table}>
                    <thead>
                      <tr>
                        <th style={tbl.th}>Logo</th>
                        <th style={tbl.th}>Name</th>
                        <th style={tbl.th}>Arabic</th>
                        <th style={tbl.th}>Source</th>
                        <th style={tbl.th}>Menu link</th>
                        <th style={tbl.th}>Sort</th>
                        <th style={tbl.th}>Status</th>
                        <th style={{ ...tbl.th, textAlign: "right" }}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {pg.paged.map((b) => (
                        <tr key={b.id}>
                          <td style={tbl.td}>
                            {b.image ? (
                              <img
                                src={b.image}
                                alt={b.name}
                                style={{ width: 34, height: 34, objectFit: "contain", borderRadius: 6, border: `1px solid ${C.border}`, background: C.white }}
                              />
                            ) : (
                              <span style={{ fontSize: 12, color: C.textLight }}>—</span>
                            )}
                          </td>
                          <td style={{ ...tbl.td, fontWeight: 600, color: C.text }}>{b.name}</td>
                          <td style={{ ...tbl.td, direction: "rtl", textAlign: "right" }}>
                            {b.arabicName || <span style={{ color: C.textLight }}>—</span>}
                          </td>
                          <td style={tbl.td}>
                            {b.merchant_brand_id ? (
                              <Badge color={C.coralDark || C.coral} bg={C.coralLight}>
                                Synced · {b.merchant_brand_id}
                              </Badge>
                            ) : (
                              <span style={{ fontSize: 12, color: C.textLight }}>Manual</span>
                            )}
                          </td>
                          <td style={tbl.td}>
                            {b.slug && b.merchantReference ? (
                              <div style={{ display: "inline-flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
                                <code style={{ fontSize: 12, color: b.active ? C.text : C.textLight }}>/b/{b.slug}</code>
                                {!b.active && (
                                  <span style={{ fontSize: 11, color: C.textLight }}>(off — brand inactive)</span>
                                )}
                                <button
                                  style={iconBtn}
                                  title={brandMenuUrl(b.merchantReference, b.slug)}
                                  onClick={() => copyLink(brandMenuUrl(b.merchantReference, b.slug))}
                                >
                                  <Copy size={12} /> Copy
                                </button>
                              </div>
                            ) : (
                              <span style={{ fontSize: 12, color: C.textLight }}>—</span>
                            )}
                          </td>
                          <td style={tbl.td}>{b.sortOrder ?? 0}</td>
                          <td style={tbl.td}>
                            {b.active ? (
                              <Badge color="#0F7B6C" bg="#E0F2F1">Active</Badge>
                            ) : (
                              <Badge color="#B42318" bg="#FEE4E2">Inactive</Badge>
                            )}
                          </td>
                          <td style={{ ...tbl.td, textAlign: "right" }}>
                            <div style={{ display: "inline-flex", gap: 6 }}>
                              <button style={iconBtn} onClick={() => setModal({ open: true, row: b })}>
                                <Edit size={13} /> Edit
                              </button>
                              {b.active ? (
                                <button style={iconBtn} onClick={() => handleDeactivate(b)}>
                                  <Power size={13} /> Deactivate
                                </button>
                              ) : (
                                <button style={iconBtn} onClick={() => handleReactivate(b)}>
                                  <Power size={13} /> Activate
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <TablePagination
                  total={pg.total}
                  page={pg.page}
                  limit={pg.limit}
                  onPageChange={pg.setPage}
                  onLimitChange={pg.setLimit}
                />
              </>
            )}
          </RCard>
        </div>
      </Container>

      <BrandModal
        isOpen={modal.open}
        toggle={() => setModal({ open: false, row: null })}
        onSaved={load}
        row={modal.row}
        merchantId={selectedMerchant}
      />
    </Fragment>
  );
};

export default BrandsList;
